import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import apiAxios from "../../../services/apiAxios";
import ConfirmationModal from "./ConfirmationModal";
import toast from "react-hot-toast";

const UPIPaymentModal = ({ isOpen, onClose, amount, orderId, businessName, onPaymentSuccess }) => {
  const [upiLink, setUpiLink] = useState("");
  const [loading, setLoading] = useState(true);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (isOpen && orderId && amount) {
      setLoading(true);
      // Fetch UPI link from backend
      apiAxios
        .post("/api/order/generate-upi", {
          amount,
          orderId,
          businessName: businessName || "Hyperapt Store",
        })
        .then((res) => {
          setUpiLink(res.data.upiLink);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Failed to generate UPI Link", err);
          toast.error("Failed to generate UPI QR code.");
          setLoading(false);
        });
    }
  }, [isOpen, orderId, amount, businessName]);

  const processMarkAsPaid = async () => {
    setShowConfirm(false);
    try {
      setMarkingPaid(true);
      const res = await apiAxios.patch(`/api/order/mark-paid/${orderId}`);
      if (res.status === 200) {
        toast.success("Order marked as paid successfully!");
        onPaymentSuccess();
        onClose();
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to mark order as paid.");
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleMarkAsPaid = () => {
    setShowConfirm(true);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-gray-900 bg-opacity-95 backdrop-blur-md">
      <div className="bg-white rounded-3xl w-full max-w-md mx-4 overflow-hidden shadow-2xl flex flex-col transform transition-all items-center p-8">
        <h2 className="text-3xl font-black text-gray-900 mb-1 tracking-tight pr-4 w-full text-center">
          Scan & Pay
        </h2>
        <p className="text-gray-500 font-medium mb-8 text-center text-sm">
          Use any UPI app (GPay, PhonePe, Paytm, etc.)
        </p>

        <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6 shadow-inner mb-8 min-h-[280px] min-w-[280px] flex items-center justify-center">
          {loading ? (
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-4 border-gray-200 border-t-primary-600 rounded-full animate-spin"></div>
              <span className="text-sm font-semibold text-gray-400">Generating QR...</span>
            </div>
          ) : upiLink ? (
            <QRCodeSVG value={upiLink} size={250} level="H" includeMargin={true} />
          ) : (
            <span className="text-red-500 font-bold">Failed to load QR</span>
          )}
        </div>

        <div className="w-full bg-gray-50 rounded-2xl p-5 mb-8 border border-gray-100">
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-500 font-bold text-sm tracking-widest uppercase">Amount Payable</span>
            <span className="text-3xl font-black text-primary-600 tracking-tight">₹{amount}</span>
          </div>
          <div className="flex justify-between items-center pt-2 border-t border-gray-200 border-dashed">
            <span className="text-gray-500 font-bold text-[11px] tracking-widest uppercase">Order ID</span>
            <span className="text-gray-800 font-bold text-sm bg-white px-2 py-1 rounded shadow-sm border border-gray-100">{orderId}</span>
          </div>
        </div>

        <div className="flex flex-col w-full gap-3 mt-auto">
          <button
            onClick={handleMarkAsPaid}
            disabled={markingPaid || loading}
            className="w-full py-4 bg-green-500 hover:bg-green-600 active:scale-95 text-white rounded-xl font-bold transition-all shadow-md text-lg disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {markingPaid ? "Confirming..." : "Mark as Paid"}
          </button>
          <button
            onClick={onClose}
            className="w-full py-3.5 bg-white border-2 border-gray-200 hover:bg-gray-50 active:scale-95 text-gray-600 rounded-xl font-bold transition-all text-sm"
          >
            Cancel & Close
          </button>
        </div>

        <ConfirmationModal 
          isOpen={showConfirm}
          title="Mark inside POS as Paid?"
          message={`Confirming this action marks the precise amount (₹${amount}) verified internally. This cannot be undone automatically once processed.`}
          onConfirm={processMarkAsPaid}
          onCancel={() => setShowConfirm(false)}
        />
      </div>
    </div>
  );
};

export default UPIPaymentModal;
